"use client"

import React, { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from "react"
import { bladeKey, bladeStackReducer, bladeTrail, type BladeEntry, type BladeSize } from "@/lib/blade-stack"

/** Renders one stack entry; receives the entry and the stack position helpers */
export type BladeRenderer<TParams = unknown> = (props: BladeRenderProps<TParams>) => ReactNode

export interface BladeRenderProps<TParams = unknown> {
  entry: BladeEntry<TParams>
  /** 0 = top of the stack */
  depth: number
  trail: string[]
  close: () => void
  popTo: (index: number) => void
}

export type BladeRegistry = Record<string, BladeRenderer<never>>

interface OpenOptions {
  id?: string
  size?: BladeSize
}

interface BladeStackApi {
  stack: BladeEntry[]
  /** Opens (or re-opens) a blade; the id defaults to the kind so one blade of a kind is open at a time */
  open: <TParams>(kind: string, label: string, params: TParams, options?: OpenOptions) => void
  close: (id: string) => void
  closeAll: () => void
  popTo: (index: number) => void
  isOpen: (id: string) => boolean
}

const BladeStackContext = createContext<BladeStackApi | null>(null)

/**
 * Holds the ordered stack of open blades and renders them through a registry of
 * kind → component, so any surface (toolbar, table row, another blade) can open a
 * blade without owning its state. One provider per page; the host renders the
 * stack at the end of the page so blades portal above the content.
 */
export function BladeStackProvider({ registry, children }: { registry: BladeRegistry; children: ReactNode }) {
  const [stack, dispatch] = useReducer(bladeStackReducer, [])

  const open = useCallback(<TParams,>(kind: string, label: string, params: TParams, options: OpenOptions = {}) => {
    dispatch({ type: "open", entry: { id: options.id ?? kind, kind, label, params, size: options.size ?? "md" } })
  }, [])
  const close = useCallback((id: string) => dispatch({ type: "close", id }), [])
  const closeAll = useCallback(() => dispatch({ type: "closeAll" }), [])
  const popTo = useCallback((index: number) => dispatch({ type: "popTo", index }), [])
  const isOpen = useCallback((id: string) => stack.some((entry) => entry.id === id), [stack])

  const api = useMemo<BladeStackApi>(() => ({ stack, open, close, closeAll, popTo, isOpen }), [stack, open, close, closeAll, popTo, isOpen])

  return (
    <BladeStackContext.Provider value={api}>
      {children}
      {stack.map((entry, index) => {
        const render = registry[entry.kind] as BladeRenderer | undefined
        if (!render) return null
        return (
          <React.Fragment key={bladeKey(entry)}>
            {render({
              entry,
              depth: stack.length - 1 - index,
              trail: bladeTrail(stack, index),
              close: () => close(entry.id),
              popTo,
            })}
          </React.Fragment>
        )
      })}
    </BladeStackContext.Provider>
  )
}

export function useBlades(): BladeStackApi {
  const api = useContext(BladeStackContext)
  if (!api) throw new Error("useBlades must be used inside a BladeStackProvider")
  return api
}
